<template>
    <div class="cielo-select__container" :class="otherClass">
        <div
            @click="handleOpenOptions"
            @keyup.enter="handleOpenOptions"
            class="cielo-select"
            :class="{ disabled, 'cielo-select--desktop': !renderMobile(), 'cielo-select--open': isOptionsOpen, 'cielo-select--phone': customClass}"
            :tabindex="disabled ? '-1' : '0'"
            role="button"
            :aria-label="$ml.get('ariaCTASelect') + label"
        >
            <small
                :class="{
                    'label-small': !this.isItemSelected,
                    'label-large': this.isItemSelected,
                }"
            >{{ label }}</small>
            <span v-if="this.isItemSelected" :class="{ selected: actualText }" class="select-text text-nowrap text-truncate">
                <span v-if="onGetItemEvent" v-text="$ml.get('DisplayText')" />
                <span v-if="!onGetItemEvent" v-text="this.canTranslate ? $ml.get(actualText) : actualText" />
                <small v-if="actualGrossAmount" v-text="`(R$ ` + actualGrossAmount + `)`" />
            </span>
            <img src="@/assets/img/icons/arrow-down-blue.svg" alt="" tabindex="-1" />
        </div>
        <CieloDropdown
            v-if="optionItems && !renderMobile()"
            :is-options-open="isOptionsOpen"
            @close-dropdown="handleOpenOptions"
            :tabindex="isOptionsOpen ? 0 : -1"
            :aria-label="$ml.get('ariaSelectList') + label"
        >
            <CieloDropdownItem
                v-for="(item, key) in optionItems"
                :key="key"
                :active="isItemActive(item)"
                @click="handleItemSelected(item)"
                @input="(value) => $emit('input-number', value)"
                :lang-options="getLangOptions(item)"
                :display-text="item.Text"
                :is-options-open="isOptionsOpen"
                :item="item"
                :can-translate="canTranslate"
            />
        </CieloDropdown>

        <CieloOverlay
            v-else-if="optionItems"
            :title="title"
            v-show="isOptionsOpen"
            @closeOverlay="isOptionsOpen = false"
            :is-options-open="isOptionsOpen"
        >
            <CieloSelectItem
                v-for="(item, key) in optionItems"
                :key="key"
                :active="isItemActive(item)"
                @click="handleItemSelected(item)"
                @input="(value) => $emit('input-number', value)"
                :lang-options="getLangOptions(item)"
                :display-text="item.Text"
                :item="item"
                :can-translate="canTranslate"
            />
        </CieloOverlay>
    </div>
</template>

<script>
    import CieloOverlay from './components/CieloOverlay';
    import CieloSelectItem from './components/CieloSelectItem';
    import CieloDropdown from './components/CieloDropdown';
    import CieloDropdownItem from './components/CieloDropdownItem';
    import { Options } from '@/config/lang/genericOptions.js';
    import { MLBuilder } from 'vue-multilanguage';
    import { isMobile } from '@/helpers/isMobile';

    export default {
        components: {
            CieloOverlay,
            CieloSelectItem,
            CieloDropdown,
            CieloDropdownItem
        },

        props: {
            disabled: {
                type: Boolean,
                default: false,
            },
            optionItems: {
                type: Array,
                required: false,
                default: () => []
            },
            title: {
                type: String,
                required: true,
            },
            label: {
                type: String,
                required: true,
            },
            selectedItem: {
                type: [String, Number],
                required: false,
                default: null,
            },
            langOptions: {
                type: Options,
                default: null,
            },
            onGetItemEvent: {
                type: Function,
                default: null,
            },
            customClass: {
                type: String,
                default: '',
            },
            otherClass: {
                type: String,
                default: '',
            },
            canTranslate: {
                type: Boolean,
                default: false
            }
        },

        computed: {
            mlDisplayText() {
                let item = this.optionItems.filter((i) => {
                return i.Value == this.selectedItem;
                });

                let options = this.onGetItemEvent(item[0]);

                if (!options) return;

                let builder = new MLBuilder(options.langParameterName);

                options.tokens.forEach((token) => {
                    builder.with(token.key, token.value);
                });

                return builder;
            },
        },

        created() {
            if (this.selectedItem) this.syncSelectedOption(this.selectedItem);
        },

        watch: {
            selectedItem(item) {
                this.syncSelectedOption(item);
            },
        },

        methods: {
            handleOpenOptions() {
                if (!this.disabled) {
                    this.isOptionsOpen = !this.isOptionsOpen;
                }
            },

            handleItemSelected(option) {
                this.isOptionsOpen = false;
                this.isItemSelected = true;
                this.updateOption(option.Text, option.Value);
                this.$emit('change', option.Value);
                this.$emit('input-number', option.Value);
                if(option.grossAmount) this.actualGrossAmount = option.grossAmount;
            },

            handleOnBlur() {
                this.isOptionsOpen = false;
            },

            deselectItem() {
                this.isOptionsOpen = false;
                this.isItemSelected = false;
                this.updateOption(null, null);
            },

            isItemActive(option) {
                return this.actualValue == option.Value;
            },

            updateOption(text, value) {
                this.actualText = text;
                this.actualValue = value;
            },

            syncSelectedOption(selectedOption) {
                if (!selectedOption) this.deselectItem();

                this.optionItems.filter((item) => {
                    if (item.Value == selectedOption) {
                        this.handleItemSelected(item);
                    }
                });
            },

            getLangOptions(item) {
                if (!this.onGetItemEvent) return;

                return this.onGetItemEvent(item);
            },

            renderMobile() {
                return isMobile('1024');
            },
        },

        data() {
            return {
                isOptionsOpen: false,
                isItemSelected: false,
                actualText: null,
                actualValue: null,
                actualGrossAmount: null,
            };
        },
    };
</script>

<style scoped lang="scss">
    .cielo-select {
        height: 50px;
        position: relative;
        border-radius: 8px;
        color: #5a646e;
        border: 1.5px solid #B8C1CC;
        background: #ffffff;
        cursor: pointer;

        &--desktop {
            &.cielo-select--open {
                top: -1px;
                border-color: #0774E7;
                border-bottom-left-radius: 0px;
                border-bottom-right-radius: 0px;

                img {
                    transform: translateY(-50%) rotate(180deg);
                    transition: transform 0.3s ease-in-out;
                    top: 50%;
                }
            }
        }

        &--phone {
            position: relative;
            border-top-right-radius: 0px;
            border-bottom-right-radius: 0px;

            @media screen and (max-width: 1024px) {
                position: relative;
                width: 100%;
                margin-bottom: 0px !important;
            }
        }

        &.disabled {
            background: #f7f7f7;

            * {
                cursor: not-allowed !important;
            }
        }

        > small,
        > span {
            cursor: pointer;
            padding: 8px;
        }

        > small.label-large {
            position: absolute;
            top: 0;
            left: 0;
            font-size: 10px;
            color: #5a646e;
            width: 100%;
            line-height: 1;
            transition: all 0.3s ease-in-out;
        }

        > small.label-small {
            font-size: 16px;
            color: #5a646e;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
        }

        .select-text {
            position: absolute;
            top: 13px;
            left: 0;
            font-size: 16px;
            color: #5a646e;
            width: 100%;
            line-height: 1.5;

            .selected {
                color: #5a646e;
            }

            small {
                margin-left: 8px;
            }
        }

        img {
            position: absolute;
            top: 50%;
            right: 8px;
            width: 20px;
            transform: translateY(-50%);
        }

        &__container {
            position: relative;
            cursor: pointer;

            &--phone {
                width: 100%;
                position: absolute;

                img {
                    left: 90px;
                }

                @media screen and (max-width: 1024px) {
                    width: 100%;
                    max-width: 112px;
                    position:initial;
                }
            }

            &--documentType {
                width: 100%;

                .cielo-select {
                    @media screen and (max-width: 1024px) {
                        margin-bottom: 0px !important;
                    }
                }
            }
        }
    }
</style>
